import React from 'react';

import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';

import { parseSearch, stringifySearch } from '@src/utils/urlHelpers';

import PageSwitcher from './PageSwitcher';

import './Pagination.scss';

const PAGES_SHOW = 7;

const chunkArray = (myArray, chunkSize) => {
    const results = [];
    while (myArray.length) {
        results.push(myArray.splice(0, chunkSize));
    }

    return results;
};

const getCurrentBatch = (batches, currentPage) => {
    let returnValue = 0;
    batches.forEach((batch, i) => {
        if (batch.includes(currentPage)) {
            returnValue = i;
        }
    });
    return returnValue;
};

const calculatePaginationState = (props, prevState = {}) => {
    const { location: { search } } = props;

    const currentPage = props.currentPage && props.currentPage > 0 ? props.currentPage : 1;
    const total = props.total || 0;

    // Early return if nothing changed (optimization)
    if (prevState.currentPage !== undefined
        && currentPage === prevState.currentPage
        && total === prevState.total
        && search === prevState.search
        && props.perPage === prevState.perPage
    ) {
        return null;
    }

    const totalPages = Math.ceil(total / props.perPage);
    const pageNumbers = totalPages > 0 ? [...Array(totalPages + 1).keys()] : [];
    const remainder = total > PAGES_SHOW ? (total - PAGES_SHOW + 1) % 4 : 0;

    let batchesArray = [];

    if (totalPages <= PAGES_SHOW) {
        batchesArray = [pageNumbers];
    } else if (totalPages <= 10) {
        batchesArray = [pageNumbers.slice(1, PAGES_SHOW)].concat([pageNumbers.slice(-4)]);
    } else if (totalPages > 10 && remainder === 0) {
        batchesArray = [pageNumbers.slice(1, PAGES_SHOW)].concat(
            chunkArray((pageNumbers.slice(-(totalPages - 6))), 4),
        );
    } else if (totalPages > 10 && remainder > 0) {
        batchesArray = [pageNumbers.slice(1, PAGES_SHOW)]
            .concat(chunkArray((pageNumbers.slice(PAGES_SHOW, -remainder)), 4))
            .concat([pageNumbers.slice(-remainder)]);
    }

    return {
        search,
        searchParams: parseSearch(search),
        currentPage,
        total,
        perPage: props.perPage,
        totalPages,
        pageNumbers,
        currentBatch: getCurrentBatch(batchesArray, currentPage),
        batches: batchesArray,
    };
};

const Pagination = ({
    currentPage = 1,
    total = 0,
    perPage = 12,
    onClick,
}) => {
    const location = useLocation();
    const history = useHistory();

    // Calculate pagination state from props
    const [paginationState, setPaginationState] = React.useState(() =>
        calculatePaginationState(
            { currentPage, total, perPage, location },
            {},
        ) || {});

    React.useEffect(() => {
        const newState = calculatePaginationState(
            { currentPage, total, perPage, location },
            paginationState,
        );

        if (newState) {
            setPaginationState(newState);
        }
    }, [currentPage, total, perPage, location, paginationState]);

    const handlePageChange = React.useCallback((newPage) => {
        if (onClick) {
            onClick(newPage);
        } else {
            const { searchParams } = paginationState;
            const updatedParams = { ...searchParams };

            if (newPage === 1) {
                delete updatedParams.page;
            } else {
                updatedParams.page = newPage;
            }

            history.push({ search: stringifySearch(updatedParams) });
        }
    }, [onClick, paginationState, history]);

    const handlePageBackOnClick = React.useCallback(() => {
        const { batches, currentBatch } = paginationState;

        if (batches && batches[currentBatch - 1]) {
            const previousPageNumber = batches[currentBatch - 1]
                [batches[currentBatch - 1].length - 1];
            handlePageChange(previousPageNumber);
        }
    }, [paginationState, handlePageChange]);

    const handlePageForwardClick = React.useCallback(() => {
        const { batches, currentBatch } = paginationState;

        if (batches && batches[currentBatch + 1]) {
            const nextPageNumber = batches[currentBatch + 1][0];
            handlePageChange(nextPageNumber);
        }
    }, [paginationState, handlePageChange]);

    const handlePageBackEnter = React.useCallback((e) => {
        if (e.key === 'Enter') {
            handlePageBackOnClick();
        }
    }, [handlePageBackOnClick]);

    const handlePageForwardEnter = React.useCallback((e) => {
        if (e.key === 'Enter') {
            handlePageForwardClick();
        }
    }, [handlePageForwardClick]);

    const handlePageNumberClick = React.useCallback((pageNumber) => {
        handlePageChange(pageNumber);
    }, [handlePageChange]);

    const {
        searchParams,
        currentPage: displayPage,
        currentBatch,
        batches,
        pageNumbers,
    } = paginationState;

    const { pathname } = location;

    return (
        <PageSwitcher
            currentBatch={currentBatch}
            batches={batches}
            currentPage={displayPage}
            pageNumbers={pageNumbers}
            pathname={pathname}
            searchParams={searchParams}
            onPageClick={handlePageNumberClick}
            onPageForwardClick={handlePageForwardClick}
            onPageBackClick={handlePageBackOnClick}
            onPageForwardEnter={handlePageForwardEnter}
            onPageBackEnter={handlePageBackEnter}
        />
    );
};

Pagination.propTypes = {
    currentPage: PropTypes.number,
    total: PropTypes.number,
    perPage: PropTypes.number,
    onClick: PropTypes.func,
};

export default React.memo(Pagination);

